import { useDispatch, useSelector } from 'react-redux';
import { useEffect } from 'react';
import axios, { AxiosError } from 'axios';
import { captureMessage } from '@sentry/browser';
import { RootState } from '../store';
import {
  RequestStatus,
  setSearchRequestStatus,
  setTextPanelError,
  setNewSearchResult,
} from '../store/textPanelContent';
import { newSelectedVersionCode, selectedBook } from '../store/textPanelContent/selectors';
import { NewSearchResult } from '../types/searchResult';

const initSearchResult: NewSearchResult = {
  query: '',
  version: '',
  results: [],
};

const useLoadSearchResults = () => {
  const dispatch = useDispatch();
  const { searchInput, newSearchResult } = useSelector(
    (state: RootState) => state.textPanelContent
  );

  const bookCode = useSelector(selectedBook);

  const textVersion = useSelector(newSelectedVersionCode);

  useEffect(() => {
    let handler: NodeJS.Timeout;
    let controller: AbortController;

    const cleanup = () => {
      controller?.abort('cancelled due to new search input');
      clearTimeout(handler);
    };

    // If returning to search mode and we have existing results matching the search input, don't make a new API call
    if (newSearchResult?.query === searchInput && newSearchResult.results.length > 0) {
      dispatch(setSearchRequestStatus(RequestStatus.succeeded));
      return cleanup;
    }

    if (searchInput.length < 3) {
      dispatch(setSearchRequestStatus(RequestStatus.init));
      dispatch(setTextPanelError(undefined));
      dispatch(setNewSearchResult(initSearchResult));
    } else if (textVersion !== undefined) {
      dispatch(setTextPanelError(undefined));

      const url = new URL(
        `${process.env.REACT_APP_DEAFBIBLESOCIETY_API_V2}/v1/deafbible/bible-text/${textVersion}/search`
      );
      url.searchParams.set('q', searchInput);
      url.searchParams.set('bookCode', bookCode ?? '');

      handler = setTimeout(async () => {
        try {
          dispatch(setSearchRequestStatus(RequestStatus.loading));
          controller = new AbortController();

          const response = await axios.get(url.toString(), {
            signal: controller.signal,
          });

          const searchResultData: NewSearchResult = {
            query: searchInput,
            version: textVersion,
            results: response.data,
          };

          if (searchResultData.results.length > 0) {
            dispatch(setSearchRequestStatus(RequestStatus.succeeded));
            dispatch(setNewSearchResult(searchResultData));
          } else {
            dispatch(setSearchRequestStatus(RequestStatus.failed));
          }
        } catch (e) {
          const error = e as AxiosError;
          if (error.code !== 'ERR_CANCELED') {
            dispatch(setTextPanelError(`Error: ${error.message}`));
          } else {
            captureMessage(
              `Error fetching search results for ${textVersion} - ${searchInput}: ${JSON.stringify(
                error
              )}`
            );
          }
        }
      }, 500);
    }

    return cleanup;
  }, [dispatch, searchInput, textVersion, newSearchResult, bookCode]);
};

export default useLoadSearchResults;
